<script setup lang="ts">
import { computed, ref } from 'vue'
import { apiService } from '@/services/apiService'
import { ref as storageRef, getDownloadURL, uploadBytes } from 'firebase/storage'
import { storage, dbRegistros } from '@/firebase/config'
import type { IAlumnoPUI, IInstitutionBasePUI, ErrorValidacionPUI } from '@shared/types/pui/base'
import BaseModal from '@/components/BaseModal.vue'
import PuiCatalogoModal from './PuiCatalogoModal.vue'
import { getCatalogoPui, getEtiquetaCampoPui, type ICatalogoPui } from './puiCatalogos'
import { useStore } from 'vuex'
import { IPUIHistory } from '@shared/types/base'
import JSZip from 'jszip'
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore'
import { convertFirestoreTimestamp } from '@/components/utils/functions'

interface IPUIHistoryWithId extends IPUIHistory {
	id: string
}

const DEFAULT_ZIP_BASENAME = 'NOMBRE_PROVISIONAL_IPES010V01'
const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const TXT_MIME = 'text/plain;charset=utf-8'

const props = defineProps<{
	institutionId: string
	baseDataInstitution: IInstitutionBasePUI
	historialDocumentos: IPUIHistoryWithId[]
}>()
const historialDocumentosRef = ref<IPUIHistoryWithId[]>(props.historialDocumentos || [])
const store = useStore()
const currentInstitution = computed(() => props.institutionId)
const registrosValidados = ref(0)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)

const archivoExcel = ref<File | undefined>(undefined)
const errores = ref<ErrorValidacionPUI[]>([])
const showCatalogoModal = ref(false)
const catalogoActivo = ref<ICatalogoPui | null>(null)
const valorCatalogo = ref('')
const processingExcel = ref(false)
const validacionEjecutada = ref(false)
const generacionExitosa = ref(false)
const excelBlob = ref<Blob | null>(null)
const txtBlob = ref<Blob | null>(null)
const txtContent = ref('')
const zipBaseName = ref(DEFAULT_ZIP_BASENAME)
const showNamingGuide = ref(true)
const showTxtPreview = ref(true)
const downloadingZip = ref(false)
const tieneArchivo = computed(() => archivoExcel.value)
const nombreArchivo = computed(() => archivoExcel.value?.name ?? '')
const curpsConError = computed(() => new Set(errores.value.map(e => e.curpAlumno)).size)

const opcionesCatalogo = () => ({
	claveInstitucion: props.baseDataInstitution?.claveInstitucion,
	carreras: props.baseDataInstitution?.carrera
})

const tieneCatalogo = (row: ErrorValidacionPUI): boolean => Boolean(getCatalogoPui(row.field, { ...opcionesCatalogo(), message: row.message }))

const abrirCatalogo = (row: ErrorValidacionPUI): void => {
	catalogoActivo.value = getCatalogoPui(row.field, { ...opcionesCatalogo(), message: row.message })
	valorCatalogo.value = row.valorRegistrado ?? ''
	showCatalogoModal.value = Boolean(catalogoActivo.value)
}
const sanitizedZipBaseName = computed(() => {
	const raw = zipBaseName.value.trim().replace(/\.(zip|txt)$/i, '')
	const cleaned = raw
		.replace(/[\\/:*?"<>|]/g, '_')
		.replace(/\s+/g, '_')
		.trim()
	return cleaned || DEFAULT_ZIP_BASENAME
})

const downloadHistorialFile = async (storagePath: string | undefined, fileName?: string): Promise<void> => {
	if (!storagePath) return
	try {
		const fileRef = storageRef(storage, storagePath)
		const url = await getDownloadURL(fileRef)
		// fetch → blob: el atributo download no aplica en URLs cross-origin de Storage
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`HTTP ${response.status}`)
		}
		const blob = await response.blob()
		await downloadBlob(blob, fileName ?? storagePath.split('/').pop() ?? 'archivo')
	} catch (downloadError) {
		console.error('Error al descargar:', downloadError)
		error.value = 'No se pudo descargar el archivo. Intenta de nuevo más tarde.'
		showErrorModal.value = true
	}
}
const uploadFile = async (file: File, defaultName: string, path: string, folder: string) => {
	const safeName = (file.name || defaultName).replace(/[\\/]/g, '_').replace(/\.\./g, '_').trim()
	const fileName = `${Date.now()}_${safeName}`
	const storagePath = storageRef(storage, `${path}/${folder}/${fileName}`)
	await uploadBytes(storagePath, file)
	return storagePath.fullPath.toString()
}

const saveRegistryPUI = async (txtName: string, excelName: string, excelFile: File, txtFile: File) => {
	const [excelPath, txtPath] = await Promise.all([
		uploadFile(excelFile, excelName, `files/institutions/${currentInstitution.value}/pui`, 'excel'),
		uploadFile(txtFile, txtName, `files/institutions/${currentInstitution.value}/pui`, 'txt')
	])
	const puiRegistry = {
		idInstitution: currentInstitution.value,
		date: Date.now(),
		user: store.state.global.user.me.curp,
		studentsCount: registrosValidados.value,
		excelPath,
		txtPath,
		fileNames: [excelName, txtName]
	}
	await apiService.post('createRegistroPUI/crear', {
		RegistroPUI: puiRegistry
	})
}

const validarArchivo = async () => {
	if (!archivoExcel.value) {
		return
	}

	const file = archivoExcel.value
	if (!file) {
		return
	}

	processingExcel.value = true
	error.value = null
	errores.value = []
	registrosValidados.value = 0
	validacionEjecutada.value = false
	resetGeneracion()

	try {
		const base64 = await fileToBase64(file)

		const result = await apiService.post<{
			code: number
			message: string
			data?: {
				alumnos: Record<string, unknown>[]
			}
		}>('procesarPUIFile', {
			excelBase64: base64
		})

		if (result.code !== 1 || !result.data?.alumnos) {
			throw new Error(result.message || 'Error al procesar el archivo Excel')
		}

		const alumnos = result.data.alumnos.map(alumno => ({
			...alumno,
			ultimoPeriodo: Boolean(alumno.ultimoPeriodo)
		})) as unknown as IAlumnoPUI[]
		console.log('alumnos', alumnos)
		registrosValidados.value = alumnos.length
		validacionEjecutada.value = true
		const erroresValidacion = await apiService.post<{
			code: number
			message: string
			data?: {
				resultValidation: any[]
			}
		}>('puiValidator', {
			alumnos,
			baseDataInstitution: props.baseDataInstitution
		})
		if (erroresValidacion.code !== 1 || !erroresValidacion.data?.resultValidation) {
			throw new Error(erroresValidacion.message || 'Error al validar los datos PUI')
		}
		errores.value = erroresValidacion.data.resultValidation

		if (erroresValidacion.data.resultValidation.length === 0) {
			const generateResult = await apiService.post<{
				code: number
				message: string
				data?: {
					alumnos: IAlumnoPUI[]
					puiExcelBase64: string
					puiTxtBase64: string
				}
			}>('generatePUI', {
				alumnos
			})

			if (generateResult.code !== 1 || !generateResult.data?.puiExcelBase64 || !generateResult.data?.puiTxtBase64) {
				throw new Error(generateResult.message || 'Error al generar los archivos PUI')
			}

			const [nextExcelBlob, nextTxtBlob] = await Promise.all([base64ToBlob(generateResult.data.puiExcelBase64, EXCEL_MIME), base64ToBlob(generateResult.data.puiTxtBase64, TXT_MIME)])
			excelBlob.value = nextExcelBlob
			txtBlob.value = nextTxtBlob
			txtContent.value = await nextTxtBlob.text()
			zipBaseName.value = DEFAULT_ZIP_BASENAME
			generacionExitosa.value = true
			showNamingGuide.value = true
			showTxtPreview.value = true
		}
	} catch (err: unknown) {
		console.error('Error al procesar el Excel:', err)
		error.value = err instanceof Error ? err.message : 'Error al procesar el archivo Excel'
		showErrorModal.value = true
	} finally {
		archivoExcel.value = undefined
		processingExcel.value = false
	}
}

const resetGeneracion = () => {
	generacionExitosa.value = false
	excelBlob.value = null
	txtBlob.value = null
	txtContent.value = ''
	zipBaseName.value = DEFAULT_ZIP_BASENAME
	downloadingZip.value = false
}

const limpiarArchivo = () => {
	archivoExcel.value = undefined
	errores.value = []
	registrosValidados.value = 0
	validacionEjecutada.value = false
	error.value = null
	resetGeneracion()
}

const obtenerHistorialDocumentos = async (idInstitution: string | undefined): Promise<void> => {
	if (!idInstitution) return

	const puiHistoryRef = collection(dbRegistros, 'PUI')
	const q = query(puiHistoryRef, orderBy('date', 'asc'), where('idInstitution', '==', idInstitution))

	const querySnapshot = await getDocs(q)

	if (querySnapshot.empty) {
		return
	}

	querySnapshot.forEach(doc => {
		const data = doc.data() as IPUIHistoryWithId
		historialDocumentosRef.value.push({
			...data,
			date: convertFirestoreTimestamp(data.date),
			id: doc.id
		})
	})
}

const descargarZip = async () => {
	const sourceExcel = excelBlob.value
	const sourceTxt = txtBlob.value
	if (!sourceExcel || !sourceTxt || downloadingZip.value) return

	downloadingZip.value = true
	error.value = null

	try {
		const baseName = sanitizedZipBaseName.value
		const txtName = `${baseName}.txt`
		const zipName = `${baseName}.zip`
		const excelName = `${baseName}.xlsx`

		const zip = new JSZip()
		zip.file(txtName, sourceTxt)
		const zipBlob = await zip.generateAsync({ type: 'blob' })

		// Descargas en serie: los navegadores suelen bloquear la 2ª si se disparan casi juntas
		await downloadBlob(zipBlob, zipName)
		await downloadBlob(sourceExcel, excelName)

		await saveRegistryPUI(txtName, excelName, new File([sourceExcel], excelName, { type: EXCEL_MIME }), new File([sourceTxt], txtName, { type: TXT_MIME }))

		successMessage.value = `Se descargaron ${zipName} y ${excelName} (${registrosValidados.value} registro(s)). El .txt interno se llama ${txtName}.`
		showSuccessModal.value = true
	} catch (downloadError: unknown) {
		console.error('Error al generar el ZIP o registrar PUI:', downloadError)
		error.value = downloadError instanceof Error ? downloadError.message : 'No se pudo completar la descarga o el registro'
		showErrorModal.value = true
	} finally {
		downloadingZip.value = false
		await obtenerHistorialDocumentos(currentInstitution.value)
		limpiarArchivo()
		showSuccessModal.value = false
		successMessage.value = null
	}
}

const downloadFileDirectly = async (templateName: string) => {
	try {
		const fileRef = storageRef(storage, `templates/${templateName}`)
		const url = await getDownloadURL(fileRef)

		const link = document.createElement('a')
		link.href = url
		link.download = templateName
		link.target = '_blank'
		link.rel = 'noopener noreferrer'
		document.body.appendChild(link)
		link.click()
		link.remove()
	} catch (downloadError) {
		console.error('Error al descargar:', downloadError)
		error.value = `No se pudo descargar la plantilla ${templateName}. Intenta de nuevo más tarde.`
		showErrorModal.value = true
	}
}
const fileToBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			const result = reader.result
			if (typeof result !== 'string') {
				reject(new Error('No se pudo leer el archivo Excel'))
				return
			}
			const commaIndex = result.indexOf(',')
			resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result)
		}
		reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo Excel'))
		reader.readAsDataURL(file)
	})

const base64ToBlob = async (base64: string, mimeType: string): Promise<Blob> => {
	const response = await fetch(`data:${mimeType};base64,${base64}`)
	return response.blob()
}

/** Tiempo para que el navegador inicie la descarga antes de liberar el blob URL / disparar la siguiente. */
const DOWNLOAD_SETTLE_MS = 1200

const downloadBlob = async (blob: Blob, fileName: string): Promise<void> => {
	const url = globalThis.URL.createObjectURL(blob)
	try {
		const link = document.createElement('a')
		link.href = url
		link.download = fileName
		link.style.display = 'none'
		document.body.appendChild(link)
		link.click()
		link.remove()
		await new Promise<void>(resolve => setTimeout(resolve, DOWNLOAD_SETTLE_MS))
	} finally {
		globalThis.URL.revokeObjectURL(url)
	}
}
</script>
<template>
	<section class="pui-guide">
		<p class="pui-guide__label">Cómo funciona</p>

		<div class="pui-guide__steps">
			<article class="pui-guide-step">
				<span class="pui-guide-step__badge">Paso 1</span>
				<div class="pui-guide-step__icon" aria-hidden="true">
					<span class="pui-guide-step__grid">
						<VaIcon name="download" size="28px" />
					</span>
				</div>
				<h3 class="pui-guide-step__title">Descarga la plantilla CERTISEP</h3>
				<p class="pui-guide-step__text">Descarga la plantilla CERTISEP para capturar tus datos conforme al layout SIIE.</p>
			</article>

			<article class="pui-guide-step">
				<span class="pui-guide-step__badge">Paso 2</span>
				<div class="pui-guide-step__icon" aria-hidden="true">
					<span class="pui-guide-step__grid">
						<span v-for="n in 16" :key="n" />
					</span>
				</div>
				<h3 class="pui-guide-step__title">Sube tu base en Excel (plantilla CERTISEP)</h3>
				<p class="pui-guide-step__text">Tu lista de estudiantes, una fila por estudiante.</p>
			</article>

			<article class="pui-guide-step">
				<span class="pui-guide-step__badge">Paso 2</span>
				<div class="pui-guide-step__icon" aria-hidden="true">
					<VaIcon name="manage_search" size="28px" />
				</div>
				<h3 class="pui-guide-step__title">Revisamos y validamos</h3>
				<p class="pui-guide-step__text">Comprobamos estructura, formato y reglas exigidas por la SEP.</p>
			</article>

			<article class="pui-guide-step">
				<span class="pui-guide-step__badge">Paso 3</span>
				<div class="pui-guide-step__icon" aria-hidden="true">
					<VaIcon name="folder_open" size="28px" />
				</div>
				<h3 class="pui-guide-step__title">Descarga los archivos .zip y .xlsx</h3>
				<p class="pui-guide-step__text">Si no hay errores, nombra el archivo y descárgalo cuando esté listo para cargar en el SIIE.</p>
			</article>
		</div>
	</section>

	<div class="validador-grid">
		<aside class="validador-panel validador-panel--info">
			<div class="validador-panel__header">
				<VaIcon name="info" class="validador-panel__icon" />
				<h2 class="validador-panel__title">¿Qué hace esta herramienta?</h2>
			</div>
			<p class="validador-panel__text">
				Revisa tus datos antes de cargarlos en la plataforma. Valida estructura, formato y reglas exigidas por la SEP para archivos destinados a la plataforma SIIE que alimentan a la PUI.
			</p>
			<div class="validador-panel__header">
				<h2 class="validador-panel__title">Plantillas CERTISEP</h2>
			</div>
			<p class="validador-panel__text">Descarga las plantillas CERTISEP para capturar tus datos conforme al layout SIIE.</p>

			<button type="button" class="validador-template-btn" @click="downloadFileDirectly('Template_PUI.xlsx')">
				<span class="validador-template-btn__icon">
					<VaIcon name="download" />
				</span>
				<span class="validador-template-btn__text">
					<strong>Descargar plantilla PUI</strong>
					<small>Se procesa la primera hoja del Excel (orden de columnas PUI)</small>
				</span>
			</button>
			<button type="button" class="validador-template-btn" style="margin-top: 10px" @click="downloadFileDirectly('Catalogo_Institucion_Carrera_Rvoe.xlsx')">
				<span class="validador-template-btn__icon">
					<VaIcon name="download" />
				</span>
				<span class="validador-template-btn__text">
					<strong>Descargar catalogo de carreras</strong>
					<small>Catalogo_Institucion_Carrera_Rvoe.xlsx</small>
				</span>
			</button>
		</aside>

		<section class="validador-panel validador-panel--upload">
			<div class="validador-panel__header">
				<VaIcon name="upload_file" class="validador-panel__icon" />
				<div>
					<h2 class="validador-panel__title">Cargar archivo</h2>
					<p class="validador-panel__hint">Formatos: .xlsx, .xls</p>
				</div>
			</div>

			<VaFileUpload
				v-model="archivoExcel"
				dropzone
				color="primary"
				type="single"
				fileTypes=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
				uploadButtonText="Seleccionar Excel"
				dropZoneText="Arrastra tu archivo aquí o haz clic para seleccionar"
				class="validador-upload" />

			<div v-if="tieneArchivo" class="validador-file-badge">
				<VaIcon name="insert_drive_file" color="success" size="small" />
				<span class="validador-file-badge__name">{{ nombreArchivo }}</span>
			</div>

			<div class="validador-actions">
				<VaButton color="primary" icon="play_arrow" :disabled="!tieneArchivo || processingExcel" @click="validarArchivo"> Validar archivo </VaButton>
				<VaButton preset="secondary" icon="delete" :disabled="!tieneArchivo || processingExcel" @click="limpiarArchivo"> Limpiar </VaButton>
			</div>
		</section>
	</div>

	<div v-if="validacionEjecutada" class="validador-stats">
		<div class="validador-stat">
			<span class="validador-stat__value">{{ registrosValidados }}</span>
			<span class="validador-stat__label">Registros procesados</span>
		</div>
		<div class="validador-stat" :class="{ 'validador-stat--ok': errores.length === 0 }">
			<span class="validador-stat__value">{{ errores.length }}</span>
			<span class="validador-stat__label">Errores encontrados</span>
		</div>
		<div class="validador-stat">
			<span class="validador-stat__value">{{ curpsConError }}</span>
			<span class="validador-stat__label">CURPs afectados</span>
		</div>
	</div>

	<section v-if="generacionExitosa" class="pui-result">
		<div class="pui-naming">
			<label class="pui-naming__label" for="pui-zip-name">Nombre del archivo </label>
			<div class="pui-naming__row">
				<input id="pui-zip-name" v-model="zipBaseName" type="text" class="pui-naming__input" spellcheck="false" autocomplete="off" />
				<span class="pui-naming__ext">.zip</span>
			</div>
			<p class="pui-naming__hint">
				El ZIP incluirá un <strong>.txt</strong> con el mismo nombre. <strong>Antes de subirlo al SIIE necesita el nombre correcto</strong> — colócalo aquí siguiendo la guía de abajo.
			</p>
		</div>

		<div class="pui-naming-guide">
			<button type="button" class="pui-naming-guide__toggle" @click="showNamingGuide = !showNamingGuide">
				<VaIcon :name="showNamingGuide ? 'expand_less' : 'expand_more'" size="small" />
				<VaIcon name="edit" size="small" />
				<span>Nombrar el archivo antes de subirlo al SIIE</span>
			</button>

			<div v-show="showNamingGuide" class="pui-naming-guide__body">
				<p class="pui-naming-guide__lead">El portal <strong>no acepta dos archivos con el mismo nombre</strong>.</p>
				<p class="pui-naming-guide__lead">Debes cambiar los dos ultimos digitos: el <strong>número de archivo</strong> y el <strong>número de versión</strong>.</p>

				<div class="pui-name-breakdown">
					<p class="pui-name-breakdown__caption">Anatomía del nombre</p>
					<div class="pui-name-strip" aria-hidden="true">
						<div class="pui-name-segment">
							<code class="pui-name-segment__chip">I00PU12026DGRIR_</code>
							<span class="pui-name-segment__label">Prefijo fijo</span>
						</div>
						<span class="pui-name-strip__join">+</span>
						<div class="pui-name-segment">
							<code class="pui-name-segment__chip">IPES</code>
							<span class="pui-name-segment__label">Etiqueta</span>
						</div>
						<span class="pui-name-strip__join">+</span>
						<div class="pui-name-segment pui-name-segment--file">
							<code class="pui-name-segment__chip">001</code>
							<span class="pui-name-segment__label">N.° de archivo</span>
						</div>
						<span class="pui-name-strip__join">+</span>
						<div class="pui-name-segment">
							<code class="pui-name-segment__chip">V</code>
							<span class="pui-name-segment__label">Versión</span>
						</div>
						<span class="pui-name-strip__join">+</span>
						<div class="pui-name-segment pui-name-segment--version">
							<code class="pui-name-segment__chip">01</code>
							<span class="pui-name-segment__label">N.° de versión</span>
						</div>
						<span class="pui-name-strip__join">+</span>
						<div class="pui-name-segment">
							<code class="pui-name-segment__chip">.zip</code>
							<span class="pui-name-segment__label">Extensión</span>
						</div>
					</div>
					<div class="pui-name-legend">
						<span class="pui-name-legend__item pui-name-legend__item--file">Cambia por cada base distinta</span>
						<span class="pui-name-legend__item pui-name-legend__item--version">Cambia por cada reintento</span>
					</div>
					<p class="pui-naming-guide__note">Esto es solo un ejemplo: copia tu prefijo desde el portal SIIE.</p>
				</div>

				<div class="pui-naming-examples">
					<p class="pui-naming-examples__label">Ejemplo de cómo numerar</p>
					<div class="pui-naming-examples__panel">
						<div class="pui-naming-examples__row">
							<span class="pui-naming-examples__scenario">1.ª base, 1.er intento</span>
							<code class="pui-naming-examples__filename">I00PU12026DGRIR_IPES<span class="pui-hl-file">001</span>V<span class="pui-hl-ver">01</span>.zip</code>
						</div>
						<div class="pui-naming-examples__row">
							<span class="pui-naming-examples__scenario">Misma base, corrección</span>
							<code class="pui-naming-examples__filename">I00PU12026DGRIR_IPES<span class="pui-hl-file">001</span>V<span class="pui-hl-ver">02</span>.zip</code>
						</div>
						<div class="pui-naming-examples__row">
							<span class="pui-naming-examples__scenario">Otra base, 1.er intento</span>
							<code class="pui-naming-examples__filename">I00PU12026DGRIR_IPES<span class="pui-hl-file">002</span>V<span class="pui-hl-ver">01</span>.zip</code>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="pui-result__actions">
			<VaButton color="primary" icon="download" :loading="downloadingZip" :disabled="downloadingZip || !zipBaseName.trim()" @click="descargarZip"> Descargar .zip </VaButton>
		</div>
	</section>

	<VaDivider class="validador-divider" />

	<section v-if="!generacionExitosa" class="validador-panel validador-panel--errors" style="margin-top: 10px">
		<div class="validador-errors-header">
			<div class="validador-panel__header">
				<VaIcon name="rule" class="validador-panel__icon validador-panel__icon--danger" />
				<div>
					<h2 class="validador-panel__title">Errores de validación</h2>
					<p class="validador-panel__hint">Detalle por CURP, campo y cómo corregirlo. Si el campo usa catálogo, abre las claves válidas.</p>
				</div>
			</div>
		</div>

		<div v-if="!validacionEjecutada" class="validador-empty validador-empty--idle">
			<VaIcon name="hourglass_empty" size="large" color="info" />
			<p class="validador-empty__title">Aún no hay resultados</p>
			<p class="validador-empty__hint">Carga un archivo y pulsa &quot;Validar archivo&quot; para revisar los datos.</p>
		</div>

		<div v-else-if="errores.length === 0" class="validador-empty validador-empty--success">
			<VaIcon name="verified" size="large" color="success" />
			<p class="validador-empty__title">Validación exitosa</p>
			<p class="validador-empty__hint">Todos los registros cumplen las reglas PUI. Asigna el nombre del ZIP y descárgalo cuando esté listo.</p>
		</div>

		<div v-else class="validador-table-wrap">
			<table class="validador-errors-table">
				<colgroup>
					<col class="validador-errors-table__col-curp" />
					<col class="validador-errors-table__col-field" />
					<col class="validador-errors-table__col-msg" />
				</colgroup>
				<thead>
					<tr>
						<th scope="col">CURP</th>
						<th scope="col">¿Dónde?</th>
						<th scope="col">¿Qué pasó y cómo se corrige?</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(row, idx) in errores" :key="`${row.curpAlumno}-${row.field}-${idx}`">
						<td>
							<code class="validador-curp">{{ row.curpAlumno || '—' }}</code>
						</td>
						<td>
							<div class="validador-field-block">
								<span class="validador-field-tag">
									<VaIcon name="report" class="validador-field-tag__icon" size="14px" />
									<span class="validador-field-tag__text">{{ getEtiquetaCampoPui(row.field) }}</span>
								</span>
								<p class="validador-captured">
									Capturaste:
									<code>{{ row.valorRegistrado?.trim() ? row.valorRegistrado : '(vacío)' }}</code>
								</p>
								<p class="validador-captured">
									Valor de referencia:
									<code>{{ row.valorReferencia ?? '—' }}</code>
								</p>
							</div>
						</td>
						<td>
							<div class="validador-error-cell">
								<span class="validador-error-msg">{{ row.message }}</span>
								<button v-if="tieneCatalogo(row)" type="button" class="validador-catalog-btn" @click="abrirCatalogo(row)">
									<VaIcon name="menu_book" size="16px" />
									Ver catálogo de valores válidos
								</button>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</section>
	<section class="validador-panel validador-panel--errors" style="margin-top: 10px">
		<div class="validador-errors-header">
			<div class="validador-panel__header">
				<VaIcon name="history" class="validador-panel__icon validador-panel__icon--danger" />
				<div>
					<h2 class="validador-panel__title">Historial de documentos generados</h2>
				</div>
			</div>
		</div>
		<div v-if="historialDocumentosRef.length === 0" class="validador-empty validador-empty--idle">
			<VaIcon name="file_present" size="large" color="info" />
			<p class="validador-empty__title">Aún no hay documentos generados</p>
		</div>

		<div v-else class="validador-table-wrap">
			<table class="validador-errors-table validador-historial-table">
				<colgroup>
					<col style="width: 14%" />
					<col style="width: 22%" />
					<col style="width: 12%" />
					<col style="width: 30%" />
					<col style="width: 22%" />
				</colgroup>
				<thead>
					<tr>
						<th scope="col">Fecha</th>
						<th scope="col">Usuario que generó el documento</th>
						<th scope="col">Cantidad de registros</th>
						<th scope="col">Archivos generados</th>
						<th scope="col">Acciones</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in historialDocumentosRef" :key="row.id">
						<td>
							<span class="validador-historial-table__date">
								{{ row.date ? new Date(row.date).toLocaleString('es-MX') : '—' }}
							</span>
						</td>
						<td>
							<span class="validador-historial-table__user">{{ row.user || '—' }}</span>
						</td>
						<td>
							<span class="validador-historial-table__count">
								{{ row.studentsCount ?? '—' }}
							</span>
						</td>
						<td>
							<span class="validador-historial-table__files">
								{{ (row.fileNames ?? []).join(', ') || '—' }}
							</span>
						</td>
						<td class="validador-historial-table__actions">
							<VaButton color="primary" icon="download" size="small" :disabled="!row.excelPath" @click="downloadHistorialFile(row.excelPath, (row.fileNames ?? [])[0])"> Descargar Excel </VaButton>
							<VaButton color="primary" icon="download" size="small" :disabled="!row.txtPath" @click="downloadHistorialFile(row.txtPath, (row.fileNames ?? [])[1])"> Descargar TXT </VaButton>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</section>
	<div>
		<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al validar el archivo'" :test-mode="false" size="medium">
			<template #body>
				<div class="validador-modal validador-modal--error">
					<div class="validador-modal__header">
						<div class="validador-modal__icon validador-modal__icon--error">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="validador-modal__title">Error</h3>
					</div>
					<p class="validador-modal__text">{{ error || 'Error al validar el archivo' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>

		<PuiCatalogoModal v-model="showCatalogoModal" :catalogo="catalogoActivo" :valor-registrado="valorCatalogo" />

		<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Validación completada'" :test-mode="false" size="medium">
			<template #body>
				<div class="validador-modal validador-modal--success">
					<div class="validador-modal__header">
						<div class="validador-modal__icon validador-modal__icon--success">
							<i class="fas fa-check-circle"></i>
						</div>
						<h3 class="validador-modal__title">Éxito</h3>
					</div>
					<p class="validador-modal__text">{{ successMessage || 'Validación completada correctamente' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
			</template>
		</BaseModal>
	</div>
</template>

<style scoped>
.validador-historial-table__actions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.35rem;
}
.validador-historial-table__date,
.validador-historial-table__user,
.validador-historial-table__count,
.validador-historial-table__files {
	font-size: 0.8125rem;
	color: #374151;
}

.pui-intro__text strong {
	color: #062342;
	font-weight: 700;
}

.pui-result {
	margin: 1.25rem 0;
	display: flex;
	flex-direction: column;
	gap: 1rem;
}
.pui-naming {
	padding: 1.15rem 1.25rem;
	border-radius: 14px;
	border: 1px solid rgba(6, 35, 66, 0.1);
	background: #fff;
	box-shadow: 0 8px 24px rgba(6, 35, 66, 0.05);
}
.pui-naming__label {
	display: block;
	margin-bottom: 0.55rem;
	font-size: 0.84rem;
	font-weight: 700;
	color: #062342;
}
.pui-naming__row {
	display: flex;
	align-items: stretch;
	gap: 0;
	max-width: 640px;
}
.pui-naming__input {
	flex: 1;
	min-width: 0;
	padding: 0.7rem 0.85rem;
	border: 1px solid rgba(6, 35, 66, 0.18);
	border-right: none;
	border-radius: 10px 0 0 10px;
	font-size: 0.88rem;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	color: #062342;
	background: #fff;
	outline: none;
}
.pui-naming__input:focus {
	border-color: rgba(36, 200, 255, 0.65);
	box-shadow: 0 0 0 3px rgba(36, 200, 255, 0.18);
	z-index: 1;
}
.pui-naming__ext {
	display: inline-flex;
	align-items: center;
	padding: 0 0.9rem;
	border: 1px solid rgba(6, 35, 66, 0.14);
	border-radius: 0 10px 10px 0;
	font-size: 0.88rem;
	font-weight: 700;
	color: #6b7280;
	background: #f8fafc;
}
.pui-naming__hint {
	margin: 0.7rem 0 0;
	font-size: 0.8rem;
	line-height: 1.55;
	color: #6b7280;
}
.pui-naming__hint strong {
	color: #062342;
}
.pui-naming-guide {
	border-radius: 14px;
	background: #fff;
	overflow: hidden;
}
.pui-naming-guide__toggle {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 0.45rem;
	padding: 0.75rem 0;
	border: none;
	background: transparent;
	color: #062342;
	font-size: 0.9rem;
	font-weight: 800;
	cursor: pointer;
	text-align: left;
}
.pui-naming-guide__toggle:hover {
	color: #169bff;
}
.pui-naming-guide__body {
	padding: 0 0 0.5rem;
}
.pui-naming-guide__lead {
	margin: 0.85rem 0 1rem;
	font-size: 0.84rem;
	line-height: 1.6;
	color: #4b5563;
}
.pui-naming-guide__lead strong {
	color: #062342;
}
.pui-name-breakdown {
	margin-bottom: 1rem;
	padding: 1rem;
	border-radius: 14px;
	border: 1px solid rgba(6, 35, 66, 0.1);
	background: linear-gradient(180deg, #f8fafc 0%, #f0f7ff 100%);
}
.pui-name-breakdown__caption {
	margin: 0 0 0.75rem;
	font-size: 0.68rem;
	font-weight: 800;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: #062342;
}
.pui-name-strip {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	justify-content: center;
	gap: 0.35rem 0.25rem;
	padding: 0.85rem 0.65rem;
	border-radius: 12px;
	background: #fff;
	border: 1px solid rgba(6, 35, 66, 0.08);
}
.pui-name-segment {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.35rem;
	min-width: 0;
}
.pui-name-segment__chip {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.42rem 0.6rem;
	border-radius: 8px;
	font-size: 0.7rem;
	font-weight: 700;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	color: #062342;
	background: #f8fafc;
	border: 1px solid rgba(6, 35, 66, 0.12);
	white-space: nowrap;
}
.pui-name-segment--file .pui-name-segment__chip {
	color: #034a6f;
	background: rgba(36, 200, 255, 0.16);
	border-color: rgba(36, 200, 255, 0.38);
	box-shadow: 0 0 0 1px rgba(36, 200, 255, 0.08);
}
.pui-name-segment--version .pui-name-segment__chip {
	color: #062342;
	background: rgba(22, 155, 255, 0.14);
	border-color: rgba(22, 155, 255, 0.32);
	box-shadow: 0 0 0 1px rgba(22, 155, 255, 0.08);
}
.pui-name-segment__label {
	max-width: 5.5rem;
	font-size: 0.62rem;
	font-weight: 600;
	line-height: 1.25;
	color: #6b7280;
	text-align: center;
}
.pui-name-strip__join {
	align-self: flex-start;
	margin-top: 0.5rem;
	font-size: 0.72rem;
	font-weight: 800;
	color: rgba(36, 200, 255, 0.85);
}
.pui-name-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 0.45rem;
	margin-top: 0.75rem;
}
.pui-name-legend__item {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.28rem 0.55rem;
	border-radius: 999px;
	font-size: 0.68rem;
	font-weight: 600;
	color: #374151;
	background: #fff;
	border: 1px solid rgba(6, 35, 66, 0.1);
}
.pui-name-legend__item::before {
	content: '';
	width: 0.45rem;
	height: 0.45rem;
	border-radius: 999px;
	flex-shrink: 0;
}
.pui-name-legend__item--file::before {
	background: rgba(36, 200, 255, 0.75);
}
.pui-name-legend__item--version::before {
	background: #169bff;
}
.pui-naming-guide__note {
	margin: 0.65rem 0 0;
	font-size: 0.75rem;
	line-height: 1.45;
	color: #6b7280;
}

.pui-naming-examples {
	margin-bottom: 1rem;
}
.pui-naming-examples__label {
	margin: 0 0 0.55rem;
	font-size: 0.68rem;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #062342;
}
.pui-naming-examples__panel {
	border-radius: 14px;
	border: 1px solid rgba(6, 35, 66, 0.1);
	background: #fff;
	overflow: hidden;
}
.pui-naming-examples__row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 0.65rem;
	padding: 0.75rem 1rem;
	border-top: 1px solid rgba(6, 35, 66, 0.08);
}
.pui-naming-examples__row:first-child {
	border-top: none;
}
.pui-naming-examples__scenario {
	flex: 1 1 9rem;
	font-size: 0.8rem;
	font-weight: 600;
	color: #374151;
}
.pui-naming-examples__filename {
	flex: 1 1 14rem;
	padding: 0.4rem 0.7rem;
	border-radius: 10px;
	font-size: 0.72rem;
	font-weight: 600;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	color: #e8f4fc;
	background: #062342;
	border: 1px solid rgba(36, 200, 255, 0.18);
	white-space: nowrap;
	overflow-x: auto;
}
.pui-hl-file {
	padding: 0.05rem 0.2rem;
	border-radius: 4px;
	color: #062342;
	background: rgba(36, 200, 255, 0.55);
}
.pui-hl-ver {
	padding: 0.05rem 0.2rem;
	border-radius: 4px;
	color: #fff;
	background: #169bff;
}

.pui-alert strong {
	color: #062342;
}

.pui-preview__code code {
	display: block;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.pui-result__actions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
}
.pui-guide {
	margin-bottom: 1.25rem;
	padding: 1.35rem 1.25rem 1.15rem;
	border-radius: 16px;
	border: 1px solid rgba(6, 35, 66, 0.1);
	background: linear-gradient(180deg, #f8fafc 0%, #f0f7ff 100%);
	box-shadow: 0 8px 24px rgba(6, 35, 66, 0.06);
}
.pui-guide__label {
	margin: 0 0 1rem;
	font-size: 0.68rem;
	font-weight: 800;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: #062342;
}
.pui-guide__steps {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.85rem;
	margin-bottom: 1rem;
}
.pui-guide-step {
	position: relative;
	padding: 1rem 0.9rem 0.95rem;
	border-radius: 14px;
	border: 1px solid rgba(6, 35, 66, 0.1);
	background: #fff;
	text-align: center;
}
.pui-guide-step__badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 4.5rem;
	margin-bottom: 0.75rem;
	padding: 0.2rem 0.65rem;
	border-radius: 999px;
	font-size: 0.68rem;
	font-weight: 800;
	color: #062342;
	background: rgba(36, 200, 255, 0.18);
	border: 1px solid rgba(36, 200, 255, 0.35);
}
.pui-guide-step__icon {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 3rem;
	margin-bottom: 0.55rem;
	color: #062342;
}
.pui-guide-step__grid {
	display: grid;
	grid-template-columns: repeat(4, 10px);
	gap: 3px;
}
.pui-guide-step__grid span {
	width: 10px;
	height: 10px;
	border-radius: 2px;
	background: rgba(36, 200, 255, 0.18);
	border: 1px solid rgba(36, 200, 255, 0.35);
}
.pui-guide-step__title {
	margin: 0 0 0.35rem;
	font-size: 0.92rem;
	font-weight: 800;
	color: #062342;
}
.pui-guide-step__text {
	margin: 0;
	font-size: 0.78rem;
	line-height: 1.45;
	color: #6b7280;
}

.pui-guide-mini-table__row span {
	padding: 0.2rem 0.25rem;
	border-radius: 4px;
	background: #fff;
	border: 1px solid rgba(6, 35, 66, 0.1);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.pui-guide-output-tags span {
	padding: 0.18rem 0.45rem;
	border-radius: 999px;
	font-size: 0.62rem;
	font-weight: 700;
	color: #374151;
	background: #fff;
	border: 1px solid rgba(6, 35, 66, 0.1);
}

.pui-guide-list li {
	position: relative;
	padding-left: 1rem;
	font-size: 0.78rem;
	line-height: 1.5;
	color: #374151;
}
.pui-guide-list li::before {
	content: '';
	position: absolute;
	left: 0;
	top: 0.42rem;
	width: 0.42rem;
	height: 0.42rem;
	border-radius: 999px;
	border: 2px solid #062342;
	background: transparent;
}

.validador-grid {
	display: grid;
	grid-template-columns: minmax(280px, 1fr) minmax(320px, 1.1fr);
	gap: 1.25rem;
}
.validador-panel {
	border: 1px solid #e5e7eb;
	border-radius: 14px;
	padding: 1.25rem;
	background: #fff;
}
.validador-panel--info {
	background: #ffffff;
}
.validador-panel--upload {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}
.validador-panel__header {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	margin-bottom: 0.85rem;
}
.validador-panel--upload .validador-panel__header,
.validador-panel--errors .validador-panel__header {
	margin-bottom: 0;
}
.validador-panel__icon {
	color: #062342;
	margin-top: 0.1rem;
	flex-shrink: 0;
}
.validador-panel__icon--danger {
	color: #b21f2e;
}
.validador-panel__title {
	margin: 0;
	font-size: 1rem;
	font-weight: 700;
	color: #062342;
}
.validador-panel__hint {
	margin: 0.2rem 0 0;
	font-size: 0.8rem;
	color: #6b7280;
}
.validador-panel__text {
	margin: 0 0 1rem;
	font-size: 0.875rem;
	line-height: 1.6;
	color: #4b5563;
}
.validador-template-btn {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 0.85rem;
	padding: 0.85rem 1rem;
	border-radius: 12px;
	border: 1px solid rgba(42, 122, 91, 0.25);
	background: #ecfdf5;
	cursor: pointer;
	transition:
		transform 0.15s ease,
		box-shadow 0.15s ease,
		border-color 0.15s ease;
	text-align: left;
}
.validador-template-btn:hover {
	transform: translateY(-1px);
	border-color: rgba(42, 122, 91, 0.45);
	box-shadow: 0 8px 20px rgba(42, 122, 91, 0.12);
}
.validador-template-btn__icon {
	width: 40px;
	height: 40px;
	border-radius: 10px;
	background: #2a7a5b;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.validador-template-btn__text {
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
	color: #14532d;
}
.validador-template-btn__text strong {
	font-size: 0.9rem;
}
.validador-template-btn__text small {
	font-size: 0.75rem;
	opacity: 0.85;
}
.validador-upload :deep(.va-file-upload__dropzone) {
	border-radius: 12px;
	border: 1px dashed rgba(6, 35, 66, 0.28);
	background: #f8fafc;
	min-height: 150px;
	transition:
		border-color 0.15s ease,
		background 0.15s ease;
}
.validador-upload :deep(.va-file-upload__dropzone:hover) {
	border-color: rgba(36, 200, 255, 0.6);
	background: #f0f7ff;
}
.validador-file-badge {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.65rem 0.85rem;
	border-radius: 10px;
	background: #f0fdf4;
	border: 1px solid rgba(42, 122, 91, 0.22);
}
.validador-file-badge__name {
	font-size: 0.84rem;
	font-weight: 600;
	color: #166534;
	word-break: break-all;
}
.validador-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	margin-top: auto;
}
.validador-stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.85rem;
}
.validador-stat {
	padding: 1rem;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	background: #f8fafc;
	text-align: center;
}
.validador-stat--ok {
	border-color: rgba(42, 122, 91, 0.25);
	background: #f0fdf4;
}
.validador-stat__value {
	display: block;
	font-size: 1.75rem;
	font-weight: 800;
	color: #062342;
	line-height: 1.1;
}
.validador-stat--ok .validador-stat__value {
	color: #2a7a5b;
}
.validador-stat__label {
	display: block;
	margin-top: 0.35rem;
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}
.validador-divider {
	margin: 0;
}
.validador-errors-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1rem;
}
.validador-errors-badge {
	flex-shrink: 0;
}
.validador-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	gap: 0.5rem;
	padding: 2.5rem 1.5rem;
	border-radius: 12px;
	border: 1px dashed rgba(6, 35, 66, 0.16);
}
.validador-empty--idle {
	background: #f8fafc;
}
.validador-empty--success {
	background: #ecfdf5;
	border-color: rgba(42, 122, 91, 0.25);
}
.validador-empty__title {
	margin: 0.25rem 0 0;
	font-size: 1rem;
	font-weight: 700;
	color: #062342;
}
.validador-empty__hint {
	margin: 0;
	max-width: 440px;
	font-size: 0.875rem;
	color: #6b7280;
	line-height: 1.5;
}
.validador-table-wrap {
	border-radius: 12px;
	overflow: auto;
	border: 1px solid #e5e7eb;
	max-height: 420px;
	background: #fff;
}
.validador-errors-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}
.validador-errors-table__col-curp {
	width: 24%;
	min-width: 11rem;
}
.validador-errors-table__col-field {
	width: 22%;
	min-width: 9rem;
}
.validador-errors-table__col-msg {
	width: 54%;
}
.validador-errors-table thead th {
	position: sticky;
	top: 0;
	z-index: 1;
	padding: 0.55rem 0.75rem;
	background: #062342;
	color: #fff;
	font-weight: 700;
	font-size: 0.68rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	text-align: left;
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.validador-errors-table tbody td {
	padding: 0.5rem 0.75rem;
	vertical-align: top;
	border-bottom: 1px solid #eef2f7;
	color: #374151;
	font-size: 0.8125rem;
	line-height: 1.4;
	word-break: break-word;
	overflow-wrap: anywhere;
}
.validador-errors-table tbody tr:nth-child(even) {
	background: #f8fafc;
}
.validador-errors-table tbody tr:hover {
	background: #f0f7ff;
}
.validador-errors-table tbody tr:last-child td {
	border-bottom: none;
}
.validador-curp {
	display: inline-block;
	max-width: 100%;
	font-size: 0.78rem;
	padding: 0.2rem 0.35rem;
	font-weight: 600;
	border-radius: 6px;
	background: #f1f5f9;
	color: #0f172a;
	word-break: break-all;
}
.validador-field-block {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.35rem;
}
.validador-captured {
	margin: 0;
	font-size: 0.72rem;
	line-height: 1.4;
	color: #6b7280;
}
.validador-captured code {
	padding: 0.05rem 0.3rem;
	border-radius: 4px;
	background: #f1f5f9;
	color: #0f172a;
	font-weight: 700;
	word-break: break-all;
}
.validador-field-tag {
	display: inline-flex;
	align-items: flex-start;
	gap: 0.3rem;
	max-width: 100%;
	padding: 0.25rem 0.45rem;
	border-radius: 6px;
	font-size: 0.7rem;
	font-weight: 700;
	line-height: 1.25;
	color: #991b1b;
	background: #fef2f2;
	border: 1px solid #fecaca;
}
.validador-field-tag__icon {
	flex-shrink: 0;
	margin-top: 0.1rem;
	color: #dc2626;
}
.validador-field-tag__text {
	min-width: 0;
	word-break: break-word;
}
.validador-error-cell {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.45rem;
}
.validador-error-msg {
	display: block;
	font-size: 0.8125rem;
	color: #374151;
	line-height: 1.4;
	font-weight: 500;
}
.validador-catalog-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.35rem 0.65rem;
	border: 1px solid rgba(6, 35, 66, 0.14);
	border-radius: 8px;
	background: #f8fafc;
	color: #062342;
	font-size: 0.72rem;
	font-weight: 700;
	cursor: pointer;
	transition:
		background 0.15s ease,
		border-color 0.15s ease;
}
.validador-catalog-btn:hover {
	background: #f0f7ff;
	border-color: rgba(36, 200, 255, 0.45);
}
.validador-modal {
	border-radius: 14px;
	padding: 1.25rem;
}
.validador-modal--error {
	background: #fee2e2;
	border: 1px solid #fecaca;
}
.validador-modal--success {
	background: #dcfce7;
	border: 1px solid #bbf7d0;
}
.validador-modal__header {
	display: flex;
	align-items: center;
	gap: 0.85rem;
	margin-bottom: 0.85rem;
}
.validador-modal__icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
}
.validador-modal__icon--error {
	background: #b21f2e;
}
.validador-modal__icon--success {
	background: #2a7a5b;
}
.validador-modal__title {
	margin: 0;
	font-size: 1.15rem;
	font-weight: 700;
}
.validador-modal--error .validador-modal__title {
	color: #991b1b;
}
.validador-modal--success .validador-modal__title {
	color: #14532d;
}
.validador-modal__text {
	margin: 0;
	font-size: 0.95rem;
	line-height: 1.55;
	color: #374151;
}
@media (max-width: 960px) {
	.validador-grid,
	.validador-stats,
	.pui-guide__steps,
	.pui-guide__details,
	.pui-naming-cols {
		grid-template-columns: 1fr;
	}
	.pui-naming-examples__row {
		flex-direction: column;
		align-items: stretch;
	}
	.pui-naming-examples__filename {
		white-space: normal;
		word-break: break-all;
	}
}
@media (max-width: 640px) {
	.pui-name-strip__join {
		display: none;
	}
	.pui-name-strip {
		justify-content: flex-start;
	}
	.validador-actions :deep(.va-button) {
		width: 100%;
	}
	.validador-errors-header {
		flex-direction: column;
	}
}
</style>
